import { Link, createFileRoute } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CircleCheckIcon,
  ExternalLinkIcon,
  InfoIcon,
  TriangleAlertIcon,
} from "lucide-react";

import { CopyButton } from "~/components/copy-button";
import { DetailField, FieldGrid } from "~/components/detail-field";
import { PageHeader } from "~/components/page-header";
import { StatusBadge } from "~/components/status-badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { errorMessage, isNotFound } from "~/lib/api";
import { EM_DASH, formatDateTime, formatIDR, orDash } from "~/lib/format";
import {
  useBridgeOrder,
  type BridgeOrderDetail,
  type BridgePaymentView,
} from "~/lib/queries";

export const Route = createFileRoute("/_authed/bridge/$shopifyOrderId")({
  component: BridgeOrderDetailPage,
});

/**
 * One Arutala order, end to end.
 *
 * Shopify order → payment link → Altura transaction → points applied, on one
 * page. The centrepiece is StatusRow: three tables record this single payment in
 * three unrelated vocabularies, and all three are shown side by side rather than
 * one being picked to display. The mismatch is the diagnostic.
 */
function BridgeOrderDetailPage() {
  const { shopifyOrderId } = Route.useParams();
  const { data, error, isPending, refetch } = useBridgeOrder(shopifyOrderId);

  if (isPending) return <DetailSkeleton />;

  if (isNotFound(error)) {
    return (
      <EmptyState
        body={
          <>
            No bridge order has the Shopify order id{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
              {shopifyOrderId}
            </code>
            .
          </>
        }
      />
    );
  }

  if (error || !data) {
    return <EmptyState body={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  return (
    <div className="space-y-5">
      <div>
        <Button variant="ghost" size="sm" render={<Link to="/bridge" />}>
          <ArrowLeftIcon className="size-3.5" />
          All bridge orders
        </Button>
      </div>

      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <span className="font-mono text-xl">
              {data.shopify_order_name || data.shopify_order_id}
            </span>
            <CopyButton value={data.shopify_order_id} label="shopify order id" />
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-2">
            <StatusBadge status={data.status} />
            <span className="text-base font-medium text-foreground tabular-nums">
              {formatIDR(data.amount_gross)}
            </span>
            <span className="text-muted-foreground">{orDash(data.customer_email)}</span>
          </span>
        }
      />

      <StatusAgreement detail={data} />
      <Overview detail={data} />
      <Payments detail={data} />
      <Points detail={data} />
    </div>
  );
}

/**
 * The three vocabularies, side by side, and whether they agree.
 *
 * This is the reason the screen exists. One real-world payment is recorded three
 * times in three unrelated status languages:
 *
 *   bridge_orders.status         CREATED · PAYMENT_LINK_SENT · PAID · …
 *   bridge_payments.status       INITIATED · COMPLETED · FAILED · …
 *   transactions.payment_status  INITIATED · SUCCESSFULL · SETTLED · …
 *
 * They are shown untranslated. Normalising them onto one scale would hide the
 * disagreement, and a bridge order stuck at PAYMENT_LINK_SENT whose transaction
 * is SUCCESSFULL is precisely the bug nobody could previously see.
 */
function StatusAgreement({ detail }: { detail: BridgeOrderDetail }) {
  const agree = detail.statuses_agree;

  return (
    <Card
      className={
        agree ? "border-success/25 bg-success/5" : "border-warning/30 bg-warning/5"
      }
    >
      <CardHeader>
        <CardTitle
          className={`flex items-center gap-2 text-base ${
            agree ? "text-success" : "text-warning"
          }`}
        >
          {agree ? (
            <CircleCheckIcon className="size-4" />
          ) : (
            <TriangleAlertIcon className="size-4" />
          )}
          {agree
            ? "All three records agree"
            : "These three records disagree about this payment"}
        </CardTitle>
        <CardDescription>
          {agree
            ? "The Shopify order, the payment and the Altura transaction tell the same story. Nothing to chase here."
            : "One real payment, recorded three times in three vocabularies — and they do not line up. That gap is usually the bug, not a display problem."}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <Vocabulary
            label="Shopify order"
            table="bridge_orders.status"
            value={detail.status}
          />
          <Vocabulary
            label="Payment"
            table="bridge_payments.status"
            value={detail.payments[0]?.status}
            extra={
              detail.payments.length > 1
                ? `${detail.payments.length} attempts — see below`
                : undefined
            }
          />
          <Vocabulary
            label="Altura transaction"
            table="transactions.payment_status"
            value={detail.payments[0]?.transaction_status}
            extra={
              detail.payments.length > 0 && !detail.payments[0]?.transaction_found
                ? "no transaction row exists"
                : undefined
            }
          />
        </div>

        {detail.payments.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No payment link has been created for this order yet, so there is nothing for the
            other two columns to agree or disagree with.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Vocabulary({
  label,
  table,
  value,
  extra,
}: {
  label: string;
  table: string;
  value: string | undefined;
  extra?: string;
}) {
  return (
    <div className="min-w-0 space-y-1.5 rounded-lg border bg-background p-3">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      {value ? (
        <StatusBadge status={value} />
      ) : (
        <span className="text-sm text-muted-foreground">{EM_DASH}</span>
      )}
      {/* The column name, on the page. Three vocabularies is confusing enough
          without having to remember which table each word came from. */}
      <p className="font-mono text-[0.6875rem] break-all text-muted-foreground">{table}</p>
      {extra ? <p className="text-xs text-muted-foreground">{extra}</p> : null}
    </div>
  );
}

function Overview({ detail }: { detail: BridgeOrderDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Shopify order</CardTitle>
      </CardHeader>
      <CardContent>
        <FieldGrid>
          <DetailField label="Shopify order ID" copy={detail.shopify_order_id} mono>
            {detail.shopify_order_id}
          </DetailField>
          <DetailField label="Order name" mono={Boolean(detail.shopify_order_name)}>
            {orDash(detail.shopify_order_name)}
          </DetailField>
          <DetailField
            label="Customer"
            copy={detail.customer_email || undefined}
            mono={false}
          >
            {orDash(detail.customer_email)}
          </DetailField>
          <DetailField
            label="Shopify customer ID"
            copy={detail.shopify_customer_id || undefined}
            mono
          >
            {orDash(detail.shopify_customer_id)}
          </DetailField>
          {/* Gross and net are bigint minor units on the column, converted
              server-side. They are NOT the same representation as the
              transaction amounts below, which start life as floats. */}
          <DetailField label="Gross">
            <span className="tabular-nums">{formatIDR(detail.amount_gross)}</span>
          </DetailField>
          <DetailField label="Net">
            <span className="tabular-nums">{formatIDR(detail.amount_net)}</span>
          </DetailField>
          <DetailField label="Created">{formatDateTime(detail.created_at)}</DetailField>
          <DetailField label="Updated">{formatDateTime(detail.updated_at)}</DetailField>
        </FieldGrid>
      </CardContent>
    </Card>
  );
}

/** Every payment link created for this order, oldest first. */
function Payments({ detail }: { detail: BridgeOrderDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Payments{" "}
          <span className="font-normal text-muted-foreground tabular-nums">
            {detail.payments.length}
          </span>
        </CardTitle>
        <CardDescription>
          Every payment link created for this order — a customer who retried has more than
          one, and the first attempt is usually the one being asked about.
        </CardDescription>
      </CardHeader>

      <CardContent>
        {detail.payments.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No payment link has been created for this order.
          </p>
        ) : (
          <ol className="space-y-3">
            {detail.payments.map((payment) => (
              <Payment key={payment.altura_transaction_id} payment={payment} />
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

function Payment({ payment }: { payment: BridgePaymentView }) {
  return (
    <li className="rounded-lg border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={payment.status} />
        <span className="text-xs text-muted-foreground">{orDash(payment.method)}</span>
        <span className="ml-auto text-xs text-muted-foreground">
          {formatDateTime(payment.created_at)}
        </span>
      </div>

      <FieldGrid className="mt-3 sm:grid-cols-2 lg:grid-cols-2">
        <DetailField label="Altura transaction">
          {/* The link both directions of this screen hang off. Not rendered as a
              link when the transaction is missing: a 404 mid-incident reads as a
              deleted order rather than as one that was never created. */}
          {payment.transaction_found ? (
            <Link
              to="/transactions/$orderId"
              params={{ orderId: payment.altura_transaction_id }}
              className="font-mono text-[0.8125rem] text-primary hover:underline"
            >
              {payment.altura_transaction_id}
            </Link>
          ) : (
            <span
              className="font-mono text-[0.8125rem] text-warning"
              title="The bridge recorded this transaction id and no transaction row exists"
            >
              {payment.altura_transaction_id} · missing
            </span>
          )}
        </DetailField>

        <DetailField label="Transaction status">
          {payment.transaction_status ? (
            <StatusBadge status={payment.transaction_status} />
          ) : (
            <span className="text-muted-foreground">{EM_DASH}</span>
          )}
        </DetailField>

        <DetailField label="Requested">
          <span className="tabular-nums">{formatIDR(payment.amount_requested)}</span>
        </DetailField>
        <DetailField label="Paid">
          <span className="tabular-nums">{formatIDR(payment.amount_paid)}</span>
        </DetailField>

        <DetailField label="Paid at">{formatDateTime(payment.paid_at)}</DetailField>
        <DetailField label="Provider">{orDash(payment.provider)}</DetailField>
      </FieldGrid>

      {payment.payment_url ? (
        <div className="mt-3 flex items-center gap-1">
          <a
            href={payment.payment_url}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            Open payment link
            <ExternalLinkIcon className="size-3" />
          </a>
          <CopyButton value={payment.payment_url} label="payment link" />
        </div>
      ) : null}
    </li>
  );
}

/**
 * Points applied to this order.
 *
 * Displayed, deliberately not interpreted. The loyalty ledger lives in
 * Semprong/Swivel; points_redemptions is only Altura's record that an offset was
 * applied. A partial reimplementation of someone else's points logic here would
 * be worse than none, because it would look authoritative.
 */
function Points({ detail }: { detail: BridgeOrderDetail }) {
  if (detail.points.length === 0) {
    return (
      <Card className="border-dashed bg-transparent shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <InfoIcon className="size-3.5 text-muted-foreground" />
            No points applied
          </CardTitle>
          <CardDescription className="text-xs">
            Nothing in points_redemptions references this order.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Points</CardTitle>
        <CardDescription>
          What Altura recorded as applied. The balance itself lives in Semprong/Swivel — this
          is not a loyalty ledger and does not try to be one.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {detail.points.map((record) => (
            <li key={record.id} className="rounded-lg border p-3">
              <FieldGrid className="sm:grid-cols-2 lg:grid-cols-4">
                <DetailField label="Points used">
                  <span className="tabular-nums">{record.points_used.toLocaleString()}</span>
                </DetailField>
                {/* A rate, not money. Rendered as the number it is rather than
                    through the currency formatter. */}
                <DetailField label="Rate">
                  <span className="tabular-nums">{record.rate}</span>
                </DetailField>
                <DetailField label="Amount offset">
                  <span className="tabular-nums">{formatIDR(record.amount_offset)}</span>
                </DetailField>
                <DetailField label="Applied">
                  {formatDateTime(record.created_at)}
                </DetailField>
              </FieldGrid>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-36 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

function EmptyState({ body, onRetry }: { body: React.ReactNode; onRetry?: () => void }) {
  return (
    <div className="mx-auto max-w-lg">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Couldn't open this bridge order</CardTitle>
          <CardDescription>{body}</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          {onRetry ? (
            <Button variant="outline" size="sm" onClick={onRetry}>
              Try again
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" render={<Link to="/bridge" />}>
            Back to the bridge
            <ArrowRightIcon className="size-3.5" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
